/**
 * איפוס סיסמה  —  /reset-password
 * היעד של הקישור מהמייל ש-ForgotPasswordPage שולח.
 */
import React, { useState } from 'react';
import { Link as RouterLink, useNavigate, useSearchParams } from 'react-router-dom';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Link,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { authService } from '../services/authService';
import { getApiError } from '../utils/apiError';

const ResetPasswordPage: React.FC = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  // The email sends ?token= (sendgrid_service.py), and this read ?key= - the
  // Israeli system's convention, which is where the page was patterned from.
  // Nobody hit it, because the link was also pointing at the patient app, and
  // that app does read token: the two defects cancelled out. Fixing only the
  // domain would have moved the link from a page that works to one that cannot
  // read the parameter, and the coordinator would see "missing its token".
  //
  // key is still accepted so a link already sitting in somebody's inbox from
  // either era keeps working.
  const key = searchParams.get('token') || searchParams.get('key') || '';

  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [error, setError] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    if (password.length < 8) {
      setError('הסיסמה צריכה להיות באורך 8 תווים לפחות');
      return;
    }
    if (password !== confirmPassword) {
      setError('הסיסמאות אינן זהות');
      return;
    }

    setSubmitting(true);
    try {
      await authService.resetPassword(key, password);
      setDone(true);
      setTimeout(() => navigate('/login', { replace: true }), 2000);
    } catch (err: any) {
      setError(getApiError(err, 'לא הצלחנו לעדכן את הסיסמה. ייתכן שתוקף הקישור פג.'));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        backgroundColor: 'background.default',
        p: 2,
      }}
    >
      <Card dir="rtl" sx={{ width: '100%', maxWidth: 420 }}>
        <CardContent sx={{ p: 4 }}>
          <Typography variant="h5" sx={{ fontWeight: 600, color: 'primary.dark', mb: 3 }}>
            בחירת סיסמה חדשה
          </Typography>

          {!key ? (
            <Stack spacing={2}>
              <Alert severity="error">בקישור חסר מפתח האיפוס.</Alert>
              <Link component={RouterLink} to="/forgot-password" variant="body2" textAlign="center">
                בקשת קישור חדש
              </Link>
            </Stack>
          ) : done ? (
            <Alert severity="success">הסיסמה עודכנה. מעבירים אותך להתחברות…</Alert>
          ) : (
            <form onSubmit={handleSubmit}>
              <Stack spacing={2}>
                {error && <Alert severity="error">{error}</Alert>}
                <TextField
                  label="סיסמה חדשה"
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  autoComplete="new-password"
                  fullWidth
                  autoFocus
                />
                <TextField
                  label="אימות סיסמה חדשה"
                  type="password"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  autoComplete="new-password"
                  fullWidth
                />
                <Button type="submit" variant="contained" disabled={submitting} fullWidth>
                  {submitting ? 'שומר…' : 'עדכון סיסמה'}
                </Button>
              </Stack>
            </form>
          )}
        </CardContent>
      </Card>
    </Box>
  );
};

export default ResetPasswordPage;
